<script setup lang="ts">
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { JobOccurrenceDto } from '@/api/generator';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import { routenames } from '@/router/routenames';
import { globalContextMenuHeaderItemDefinition } from '@/components/ContextMenu/utils';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import { DataTableHeader, SortItem } from '@/components/OverviewTable/types';
import { formatTimePeriod } from '@/components/formatters/utils';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import Due from '../Due.vue';
import { EntityChangeEvent, getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import AssignedUsersRoles from '@/components/AssignedUsersRoles/AssignedUsersRoles.vue';
import JobExecutionProgress from '../JobExecutionProgress.vue';
import { displayValue } from '@/components/forms/utils';
import { ServerSidePaginationOptions } from '@/utils/useServerSideOdataPagination';
import { useUserConstants } from '@/utils/useUserConstants';
import { JobOccurrenceTableColumn, mapTableToDtoKeys } from './jobOccurrenceTable';

const { t } = useTranslation();
const router = useRouter();
const { systemUser } = useUserConstants();

export interface JobOccurrenceTableProps {
  jobOccurrences: JobOccurrenceDto[];
  columns?: JobOccurrenceTableColumn[];
  loading?: boolean;
  serverSidePagination?: boolean;
  itemsLength?: number;
  hideDueBadge?: boolean;
  sortable?: boolean;
  mustSort?: boolean;
  showPeriodTime?: boolean;
  defaultSorting?: SortItem<JobOccurrenceTableColumn>[];
  height?: string | number;
  fixedHeader?: boolean;
}

const props = withDefaults(defineProps<JobOccurrenceTableProps>(), {
  columns: () => [
    'jobConfigurationName',
    'targetSystem.name',
    'frequency',
    'assigned',
    'review',
    'completedByUser.name',
    'progress',
    'state',
    'contextMenu',
  ],
  loading: false,
  serverSidePagination: false,
  itemsLength: 0,
  hideDueBadge: false,
  sortable: true,
  mustSort: false,
  showPeriodTime: false,
  defaultSorting: () => [{ key: 'period', order: 'asc' }],
  height: undefined,
  fixedHeader: false,
});

const emits = defineEmits<{
  'update:options': [options: ServerSidePaginationOptions];
}>();

function updateOptions(options: ServerSidePaginationOptions<JobOccurrenceTableColumn>) {
  let sortBy = options.sortBy;

  // custom sort for Status column
  sortBy = sortBy?.flatMap((sort) => {
    if (sort.key === 'state') {
      return [sort, { key: 'lastModificationTime' as JobOccurrenceTableColumn, order: sort.order }];
    }
    return [sort];
  });

  emits('update:options', {
    ...options,
    sortBy: mapTableToDtoKeys(sortBy),
  });
}

const headers = computed<DataTableHeader<JobOccurrenceTableColumn>[]>(() =>
  (
    [
      {
        title: t('Job_one'),
        key: 'jobConfigurationName',
        sortable: props.sortable,
      },
      {
        title: t('MedicalSystem_one'),
        key: 'targetSystem.name',
        sortable: props.sortable,
      },
      {
        title: t('Recurrence'),
        key: 'frequency',
        sortable: props.sortable,
      },
      {
        title: t('JobOccurrencePeriodOfExecution'),
        key: 'period',
        sortable: props.sortable,
      },
      {
        title: t('Assignment'),
        key: 'assigned',
        sortable: false,
      },
      {
        title: t('ReviewAssignment'),
        key: 'review',
        sortable: false,
      },
      {
        title: t('JobExecutionCompletedBy'),
        key: 'completedByUser.name',
        sortable: props.sortable,
      },
      {
        title: t('StateOfExecution'),
        key: 'state',
        sortable: props.sortable,
      },
      {
        title: t('ApprovedBy'),
        key: 'approvedBy',
        sortable: props.sortable,
      },
      {
        title: t('JobExecutionProgress'),
        key: 'progress',
        sortable: false,
      },
      globalContextMenuHeaderItemDefinition,
    ] satisfies DataTableHeader<JobOccurrenceTableColumn>[]
  ).filter(({ key }) => props.columns.includes(key)),
);

function navigateTo(
  _event: Event,
  row: { item: JobOccurrenceDto },
  hash: string = `#${TAB_PREFIX}workitems`, // force navigate to workitems-tab
) {
  router.push({
    name: routenames.jobOccurrences.execution,
    hash,
    params: {
      id: row.item.id,
    },
  });
}

function getApprovedByEntityChange(item: JobOccurrenceDto) {
  const isApproved = item.reviewRequired && item.state === 'Approved';

  if (!isApproved) {
    return undefined;
  }

  let result: EntityChangeEvent | undefined;
  if (item.approvalTime) {
    // if approverUser is null/undefined, it was auto-approved by system
    const approverUser = item.approverUser || systemUser;
    result = {
      user: approverUser.fullName,
      time: item.approvalTime,
      type: 'modified' as const,
    };
  }
  return result;
}
</script>

<template>
  <OverviewTable
    class="mt-2"
    :itemsPerPage="20"
    :serverSidePagination="serverSidePagination"
    :headers="headers"
    :items="jobOccurrences"
    :itemsLength="itemsLength"
    :hideDefaultFooter="false"
    :defaultSortBy="defaultSorting"
    :mustSort="mustSort"
    :loading="loading"
    :height="height"
    :fixedHeader="fixedHeader"
    itemLabel="JobOccurrence"
    data-testid="DataTable_Jobs"
    autoWidth
    @update:options="updateOptions"
    @click:row="navigateTo"
  >
    <template #header.jobConfigurationName="headerProps">
      <OverviewTableHeader v-bind="headerProps">
        {{ $t('Job_one') }}
        <span class="font-weight-medium">| {{ $t('ID') }}</span>
      </OverviewTableHeader>
    </template>
    <template #item.jobConfigurationName="{ item }">
      <div>
        <IdentifierLabel
          :label="displayValue(item?.jobConfigurationName)"
          :identifier="item.jobConfigurationIuid"
        />
      </div>

      <div>
        <Due
          v-if="!props.hideDueBadge"
          :state="item.dueState"
          :startDate="item.start ? DateTime.fromISO(item.start) : null"
          :endDate="item.end ? DateTime.fromISO(item.end) : null"
          class="mr-1"
        />
        <CommentCount
          v-if="item.commentCount"
          :value="item"
          @click.stop="(event: Event) => navigateTo(event, { item }, `#${TAB_PREFIX}comments`)"
        />
      </div>
    </template>

    <template #item.frequency="{ item: { frequency } }">
      {{ $t(`FrequencyType.${frequency ?? 'None'}`) }}
    </template>

    <template #item.assigned="{ item }">
      <AssignedUsersRoles
        v-if="item.assignedUsers?.length || item.assignedRoles?.length"
        :users="item.assignedUsers"
        :roles="item.assignedRoles"
      />
      <span v-else>{{ displayValue(null) }}</span>
    </template>

    <template #item.review="{ item }">
      <AssignedUsersRoles
        v-if="item.reviewUsers?.length || item.reviewRoles?.length"
        :users="item.reviewUsers"
        :roles="item.reviewRoles"
      />
      <span v-else>{{ displayValue(null) }}</span>
    </template>

    <template #item.completedByUser.name="{ item }">
      <EntityChange :entityChange="getEntityChangeEvent(item, 'completed')" />
    </template>

    <template #item.period="{ item: { start, end, executionAllDay } }">
      {{ formatTimePeriod(start, end, showPeriodTime && !executionAllDay) }}
    </template>

    <template #item.state="{ item }">
      <div class="font-weight-bold">{{ $t(`StateOfUseEnum.${item.state ?? 'New'}`) }}</div>

      <!-- Note: Here we want to show changes in the execution state, not changes in the underlying JobConfigurationDTO.-->
      <EntityChange
        :entityChange="{
          user: item.executionStateChangedUser?.fullName ?? '',
          time: item.executionStateChangedTime ?? '',
          type: 'editing',
        }"
        :showEmptyFieldSign="false"
      />
    </template>

    <template #item.approvedBy="{ item }">
      <EntityChange :entityChange="getApprovedByEntityChange(item)" :showEmptyFieldSign="true" />
    </template>

    <template #item.progress="{ item }">
      <JobExecutionProgress
        v-if="
          item.workProgressSummary && Object.values(item.workProgressSummary).some((v) => v > 0)
        "
        :value="item.workProgressSummary"
      />
      <span v-else>{{ displayValue(null) }}</span>
    </template>

    <template #item.targetSystem.name="{ item }">
      {{ displayValue(item.targetSystem?.name) }}
    </template>

    <template #item.contextMenu="{ item }">
      <div class="d-flex justify-center">
        <slot name="actions" :item="item" />
      </div>
    </template>
  </OverviewTable>
</template>
